import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Tabs,
    Tab,
    TableRow,
    TableCell,
    tableCellClasses,
    IconButton,
    TextField,
    MenuItem,
    Button,
    Chip,
    Tooltip,
    Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@emotion/react';
import { useDispatch } from 'react-redux';
import { showLoader } from '../Redux/Slices/loaderSlice';
import {
    getDocuments,
    get_transaction_list,
    getAllClaims,
    getAllEligibleMembers,
    getMembersSummary,
    getTopReferrers,
    get_company_member_list,
} from '../Redux/Actions/action';
import ErrorHandler from '../Utils/handler';
import { toast } from 'react-toastify';
import CommonTable from '../Components/Common/CommonTable';
import DataNotFound from '../Components/Common/DataNotFound';
import CustomPagination from '../Components/Common/CommonPagination';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    verticalTab: {
        '&.MuiTabs-vertical': {
            borderRight: '0px solid',
        },
        '& .MuiTab-root': {
            backgroundColor: '#fff',
            textTransform: 'capitalize',
            alignItems: 'flex-start',
            marginBottom: '1px',
            borderRadius: '0px',
        },
        '& .MuiTab-root.Mui-selected': {
            backgroundColor: '#3E4094',
            color: '#fff',
        },
        '& .MuiTabs-indicator': {
            width: '0px',
        },
    },
    filterBox: {
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: '16px',
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme?.palette?.bgLightWhite?.main,
        fontSize: 14,
        textAlign: 'center',
        lineHeight: '1.2rem',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: 'center',
        padding: '8px',
    },
}));

const Reports = () => {
    const { classes } = useStyles();
    const theme = useTheme();
    const dispatch = useDispatch();

    // State
    const [selectedTab, setSelectedTab] = useState(0);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [reportData, setReportData] = useState({ data: [], count: 0 });
    const [filters, setFilters] = useState({
        status: '',
        fromDate: '',
        toDate: '',
        searchText: '',
    });

    const reportTypes = [
        { label: 'Member Registration', key: 'registration' },
        { label: 'Family Members', key: 'family' },
        { label: 'Referral Tracking', key: 'referrals' },
        { label: 'Transactions', key: 'transactions' },
        { label: 'Claims', key: 'claims' },
        { label: 'Eligible Members', key: 'eligible' },
        { label: 'Payment Requests', key: 'payments' },
    ];

    const statusOptions = {
        registration: ['Pending', 'Completed', 'Approved', 'Rejected', 'Partial-Rejected', 'Re-Upload', 'Submit-To-Office'],
        family: ['All', 'Has Family', 'No Family'],
        referrals: ['All', 'PPSK Member', 'Non-Member'],
        transactions: ['Success', 'Failed', 'Pending'],
        claims: ['Pending', 'Approved', 'Rejected', 'Processing'],
        eligible: ['Active', 'Expired'],
        payments: ['Pending', 'Approved', 'Rejected'],
    };

    const headersByType = {
        registration: ['No', 'PPSK ID', 'Name', 'Mobile', 'Added By', 'Family Members', 'Status', 'Created Date'],
        family: ['No', 'PPSK ID', 'Name', 'Mobile', 'Added By', 'Linked Family Members', 'Linked By', 'Status'],
        referrals: ['No', 'Referrer Phone', 'Referrer Name', 'Is PPSK Member', 'Total Referred', 'Top Referred Members'],
        transactions: ['No', 'Transaction ID', 'PPSK ID', 'Name', 'Amount', 'Status', 'Date'],
        claims: ['No', 'PPSK ID', 'Name', 'Claim Amount', 'Death Date', 'Status', 'Claim Date'],
        eligible: ['No', 'PPSK ID', 'Name', 'Mobile', 'Coverage Amount', 'Start Date', 'End Date'],
        payments: ['No', 'PPSK ID', 'Name', 'Amount', 'Status', 'Request Date'],
    };

    // Handlers
    const handleTabChange = (e, newValue) => {
        setSelectedTab(newValue);
        setPage(1);
        setFilters({ status: '', fromDate: '', toDate: '', searchText: '' });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleApplyFilters = () => {
        setPage(1);
        fetchReportData();
    };

    const handleResetFilters = () => {
        setFilters({ status: '', fromDate: '', toDate: '', searchText: '' });
        setPage(1);
    };

    // Fetch Data
    const fetchReportData = async () => {
        dispatch(showLoader(true));
        const currentType = reportTypes[selectedTab].key;

        try {
            let result;
            const body = {
                page,
                limit: rowsPerPage,
                ...(filters.status && { status: filters.status }),
                ...(filters.fromDate && { fromDate: filters.fromDate }),
                ...(filters.toDate && { toDate: filters.toDate }),
                ...(filters.searchText && { search: filters.searchText }),
            };

            switch (currentType) {
                case 'registration':
                    result = await dispatch(get_company_member_list({
                        ...body,
                        verificationStatus: filters.status || undefined,
                    }));
                    break;
                case 'family':
                    result = await dispatch(get_company_member_list({
                        ...body,
                        verificationStatus: 'Approved',
                    }));
                    break;
                case 'referrals':
                    result = await dispatch(getTopReferrers({
                        page,
                        limit: rowsPerPage,
                        search: filters.searchText || undefined,
                    }));
                    break;
                case 'transactions':
                    result = await dispatch(get_transaction_list(body));
                    break;
                case 'claims':
                    result = await dispatch(getAllClaims({ ...body, claimStatus: filters.status || undefined }));
                    break;
                case 'eligible':
                    result = await dispatch(getAllEligibleMembers(body));
                    break;
                case 'payments':
                    result = await dispatch(get_transaction_list({ ...body, type: 'payment_request' }));
                    break;
                default:
                    result = await dispatch(get_company_member_list(body));
            }

            const success = ErrorHandler(result, false);
            if (success) {
                const data = result?.payload?.data;
                let reportItems = [];
                let totalCount = 0;

                if (currentType === 'referrals') {
                    reportItems = data?.referrers || [];
                    totalCount = data?.pagination?.total || 0;
                } else if (currentType === 'registration' || currentType === 'family') {
                    reportItems = data?.members || [];
                    totalCount = data?.count || 0;
                } else if (currentType === 'transactions' || currentType === 'payments') {
                    reportItems = data?.transactions || [];
                    totalCount = data?.totalCount || data?.count || 0;
                } else if (currentType === 'claims') {
                    reportItems = data?.response || [];
                    totalCount = data?.count || 0;
                } else if (currentType === 'eligible') {
                    reportItems = data?.eligibleMembers || [];
                    totalCount = data?.count || 0;
                } else {
                    reportItems = data?.members || data?.response || data?.transactions || [];
                    totalCount = data?.count || data?.totalCount || 0;
                }

                setReportData({
                    data: reportItems,
                    count: totalCount,
                });
            } else {
                setReportData({ data: [], count: 0 });
            }
        } catch (error) {
            console.error('Report fetch error:', error);
            setReportData({ data: [], count: 0 });
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        fetchReportData();
    }, [selectedTab, page, rowsPerPage]);

    // Export Functions
    const exportToExcel = async () => {
        dispatch(showLoader(true));
        try {
            const currentType = reportTypes[selectedTab].key;
            const headers = headersByType[currentType];
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet(`${reportTypes[selectedTab].label} Report`);

            // Add title
            const lastCol = String.fromCharCode(64 + headers.length);
            worksheet.mergeCells(`A1:${lastCol}1`);
            worksheet.getCell('A1').value = `${reportTypes[selectedTab].label} Report`;
            worksheet.getCell('A1').font = { bold: true, size: 16 };
            worksheet.getCell('A1').alignment = { horizontal: 'center' };

            // Add date range and filters info
            worksheet.mergeCells(`A2:${lastCol}2`);
            let filterInfo = `Generated on: ${dayjs().format('DD/MM/YYYY HH:mm')}`;
            if (filters.fromDate || filters.toDate) {
                filterInfo += ` | Date Range: ${filters.fromDate || 'Start'} to ${filters.toDate || 'End'}`;
            }
            if (filters.status) {
                filterInfo += ` | Status: ${filters.status}`;
            }
            worksheet.getCell('A2').value = filterInfo;
            worksheet.getCell('A2').alignment = { horizontal: 'center' };

            // Add empty row
            worksheet.addRow([]);

            // Add headers
            const headerRow = worksheet.addRow(headers);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '3E4094' } };
                cell.alignment = { horizontal: 'center' };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                };
            });

            // Add data
            reportData.data.forEach((item, index) => {
                const rowData = getRowData(item, index, currentType);
                const dataRow = worksheet.addRow(rowData);
                dataRow.eachCell((cell) => {
                    cell.alignment = { horizontal: 'center' };
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' },
                    };
                });
            });

            // Auto-fit columns with better widths
            worksheet.columns.forEach((column, index) => {
                let maxLength = headers[index]?.length || 10;
                column.eachCell({ includeEmpty: true }, (cell) => {
                    const cellLength = cell.value ? String(cell.value).length : 0;
                    if (cellLength > maxLength) {
                        maxLength = cellLength;
                    }
                });
                column.width = Math.min(maxLength + 2, 40);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `${reportTypes[selectedTab].label.replace(/\s+/g, '_')}_Report_${dayjs().format('YYYY-MM-DD')}.xlsx`);
            toast.success('Excel exported successfully');
        } catch (error) {
            toast.error('Failed to export Excel');
            console.error(error);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const exportToPDF = async () => {
        dispatch(showLoader(true));
        try {
            const currentType = reportTypes[selectedTab].key;
            const headers = headersByType[currentType];
            
            // Create CSV content
            let csvContent = `${reportTypes[selectedTab].label} Report\n`;
            csvContent += `Generated on: ${dayjs().format('DD/MM/YYYY HH:mm')}\n`;
            if (filters.fromDate || filters.toDate) {
                csvContent += `Date Range: ${filters.fromDate || 'Start'} to ${filters.toDate || 'End'}\n`;
            }
            if (filters.status) {
                csvContent += `Status Filter: ${filters.status}\n`;
            }
            csvContent += `Total Records: ${reportData.count}\n\n`;
            csvContent += headers.join(',') + '\n';
            
            reportData.data.forEach((item, index) => {
                const rowData = getRowData(item, index, currentType);
                csvContent += rowData.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',') + '\n';
            });

            // Create and download as CSV
            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `${reportTypes[selectedTab].label.replace(/\s+/g, '_')}_Report_${dayjs().format('YYYY-MM-DD')}.csv`);
            toast.success('CSV exported successfully');
        } catch (error) {
            toast.error('Failed to export CSV');
            console.error(error);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const getRowData = (item, index, type) => {
        const rowNum = (index % rowsPerPage) + 1 + (page - 1) * rowsPerPage;

        switch (type) {
            case 'registration':
                return [
                    rowNum,
                    item?.ppskId || '-',
                    item?.fullName || item?.personalDetails?.fullName || '-',
                    item?.phoneNumber || item?.personalDetails?.phoneNumber || '-',
                    item?.addedByUser?.fullName || 'Self',
                    item?.familyMemberCount || 0,
                    item?.verificationStatus || '-',
                    dayjs(item?.createdAt).format('DD/MM/YYYY') || '-',
                ];
            case 'family':
                const linkedMembers = item?.linkedFamilyMembers?.map(m => m?.fullName).join(', ') || '-';
                const linkedBy = item?.linkedByUsers?.map(u => u?.fullName).join(', ') || '-';
                return [
                    rowNum,
                    item?.ppskId || '-',
                    item?.fullName || item?.personalDetails?.fullName || '-',
                    item?.phoneNumber || item?.personalDetails?.phoneNumber || '-',
                    item?.addedByUser?.fullName || 'Self',
                    linkedMembers,
                    linkedBy,
                    item?.verificationStatus || '-',
                ];
            case 'referrals':
                const topReferred = item?.referredMembers?.slice(0, 3)?.map(m => m?.fullName).join(', ') || '-';
                return [
                    rowNum,
                    item?.referrerPhone || '-',
                    item?.referrerDetails?.fullName || 'Non-Member',
                    item?.isPPSKMember ? 'Yes' : 'No',
                    item?.referralCount || 0,
                    topReferred,
                ];
            case 'transactions':
                return [
                    rowNum,
                    item?.transactionId || item?._id || '-',
                    item?.ppskId || item?.userDetails?.ppskId || '-',
                    item?.userDetails?.fullName || item?.name || '-',
                    `₹${item?.amount || 0}`,
                    item?.status || '-',
                    dayjs(item?.createdAt).format('DD/MM/YYYY') || '-',
                ];
            case 'claims':
                return [
                    rowNum,
                    item?.ppskId || item?.userDetails?.ppskId || '-',
                    item?.userDetails?.fullName || '-',
                    `₹${item?.claimAmount || 0}`,
                    item?.deathDate ? dayjs(item?.deathDate).format('DD/MM/YYYY') : '-',
                    item?.status || item?.claimStatus || '-',
                    dayjs(item?.createdAt).format('DD/MM/YYYY') || '-',
                ];
            case 'eligible':
                return [
                    rowNum,
                    item?.ppskId || '-',
                    item?.fullName || item?.personalDetails?.fullName || '-',
                    item?.phoneNumber || item?.personalDetails?.phoneNumber || '-',
                    `₹${item?.claimsDetails?.[0]?.claimAmount || 100000}`,
                    item?.policyStatus?.activeAt ? dayjs(item?.policyStatus?.activeAt).format('DD/MM/YYYY') : '-',
                    item?.policyStatus?.expireAt ? dayjs(item?.policyStatus?.expireAt).format('DD/MM/YYYY') : '-',
                ];
            case 'payments':
                return [
                    rowNum,
                    item?.ppskId || item?.userDetails?.ppskId || '-',
                    item?.userDetails?.fullName || '-',
                    `₹${item?.amount || 0}`,
                    item?.status || '-',
                    dayjs(item?.createdAt).format('DD/MM/YYYY') || '-',
                ];
            default:
                return [rowNum, '-', '-', '-', '-', '-', '-'];
        }
    };

    const getStatusColor = (status) => {
        const statusColors = {
            Pending: 'warning',
            Completed: 'info',
            Approved: 'success',
            Rejected: 'error',
            Success: 'success',
            SUCCESS: 'success',
            Failed: 'error',
            FAILURE: 'error',
            Active: 'success',
            Expired: 'error',
            Processing: 'info',
            'Submit-To-Office': 'info',
            'Re-Upload': 'warning',
            'Partial-Rejected': 'warning',
        };
        return statusColors[status] || 'default';
    };

    const getStatusColumnIndex = (type) => {
        switch (type) {
            case 'registration': return 6;
            case 'family': return 7;
            case 'referrals': return -1; // No status column
            case 'transactions': return 5;
            case 'claims': return 5;
            case 'eligible': return -1; // No status column
            case 'payments': return 4;
            default: return -1;
        }
    };

    const renderTableRow = (item, index) => {
        const currentType = reportTypes[selectedTab].key;
        const rowData = getRowData(item, index, currentType);
        const statusColIndex = getStatusColumnIndex(currentType);

        return (
            <TableRow key={item._id || item?.referrerPhone || index}>
                {rowData.map((cell, cellIndex) => (
                    <StyledTableCell key={cellIndex}>
                        {cellIndex === statusColIndex ? (
                            <Chip
                                label={cell}
                                color={getStatusColor(cell)}
                                size="small"
                                sx={{ minWidth: '80px' }}
                            />
                        ) : currentType === 'referrals' && cellIndex === 3 ? (
                            <Chip
                                label={cell}
                                color={cell === 'Yes' ? 'success' : 'default'}
                                size="small"
                                variant="outlined"
                            />
                        ) : currentType === 'family' && (cellIndex === 5 || cellIndex === 6) ? (
                            <Tooltip title={cell !== '-' ? cell : ''}>
                                <span style={{ 
                                    maxWidth: '150px', 
                                    display: 'inline-block', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap' 
                                }}>
                                    {cell}
                                </span>
                            </Tooltip>
                        ) : (currentType === 'registration' || currentType === 'family') && cellIndex === 5 && typeof cell === 'number' ? (
                            <Chip
                                label={cell > 0 ? `${cell} member${cell > 1 ? 's' : ''}` : '-'}
                                color={cell > 0 ? 'primary' : 'default'}
                                size="small"
                                variant="outlined"
                            />
                        ) : (
                            cell
                        )}
                    </StyledTableCell>
                ))}
            </TableRow>
        );
    };

    return (
        <Box>
            <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={600}>
                        Reports
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            startIcon={<TableChartIcon />}
                            onClick={exportToExcel}
                            disabled={reportData.data.length === 0}
                        >
                            Export Excel
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={exportToPDF}
                            disabled={reportData.data.length === 0}
                            color="error"
                        >
                            Export CSV
                        </Button>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Sidebar Tabs */}
                    <Grid item xs={12} md={3} lg={2}>
                        <Tabs
                            className={classes.verticalTab}
                            orientation="vertical"
                            value={selectedTab}
                            onChange={handleTabChange}
                            sx={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}
                        >
                            {reportTypes.map((report, index) => (
                                <Tab
                                    key={report.key}
                                    label={report.label}
                                    value={index}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: selectedTab === index ? '#4A3AFF' : '#f0f0f0',
                                        },
                                        borderBottom: index !== reportTypes.length - 1 && '1px solid #f0f0f0',
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Grid>

                    {/* Main Content */}
                    <Grid item xs={12} md={9} lg={10}>
                        {/* Filters */}
                        <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9', borderRadius: '12px' }}>
                            <Box className={classes.filterBox}>
                                <FilterListIcon color="primary" />
                                <TextField
                                    select
                                    label="Status"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {statusOptions[reportTypes[selectedTab].key]?.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    type="date"
                                    label="From Date"
                                    value={filters.fromDate}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 150 }}
                                />
                                <TextField
                                    type="date"
                                    label="To Date"
                                    value={filters.toDate}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 150 }}
                                />
                                <TextField
                                    label="Search"
                                    value={filters.searchText}
                                    onChange={(e) => handleFilterChange('searchText', e.target.value)}
                                    size="small"
                                    placeholder="PPSK ID, Name..."
                                    sx={{ minWidth: 180 }}
                                />
                                <Button variant="contained" onClick={handleApplyFilters} size="small">
                                    Apply
                                </Button>
                                <IconButton onClick={handleResetFilters} color="primary">
                                    <RefreshIcon />
                                </IconButton>
                            </Box>
                        </Paper>

                        {/* Table */}
                        <CommonTable headerList={headersByType[reportTypes[selectedTab].key]} minWidth={800}>
                            {reportData.data.length > 0 ? (
                                reportData.data.map((item, index) => renderTableRow(item, index))
                            ) : (
                                <TableRow>
                                    <StyledTableCell colSpan={headersByType[reportTypes[selectedTab].key].length}>
                                        <DataNotFound
                                            title="No Records Found"
                                            icon={<ErrorOutlineIcon color="primary" style={{ fontSize: '3rem' }} />}
                                            elevation={0}
                                        />
                                    </StyledTableCell>
                                </TableRow>
                            )}
                        </CommonTable>

                        {/* Pagination */}
                        {reportData.count > 0 && (
                            <Box mt={2}>
                                <CustomPagination
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    count={reportData.count}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default Reports;
